import { useRef } from 'react';
import { motion, useScroll, useTransform, useSpring } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Building2, 
  Wrench, 
  Droplets, 
  ThermometerSun, 
  Sparkles,
  ArrowRight 
} from 'lucide-react';

const services = [
  {
    id: 'construccion',
    icon: Building2,
    title: 'Construcción',
    description: 'Diseñamos y construimos piscinas a medida con acabados premium.',
    image: 'https://images.unsplash.com/photo-1730135875620-795bca5badc8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBzd2ltbWluZyUyMHBvb2wlMjBtb2Rlcm4lMjB2aWxsYSUyMGJyaWdodCUyMGRheWxpZ2h0fGVufDB8fHx8MTc3MzMxNDg4Nnww&ixlib=rb-4.1.0&q=85',
    color: 'from-sky-500 to-blue-600',
  },
  {
    id: 'mantenimiento',
    icon: Sparkles,
    title: 'Mantenimiento',
    description: 'Limpieza, control de pH y revisión de equipos periódicamente.',
    image: 'https://images.unsplash.com/photo-1559732662-057ea8cffb06?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwxfHxwb29sJTIwY2xlYW5pbmclMjBuZXQlMjBibHVlJTIwd2F0ZXJ8ZW58MHx8fHwxNzczMzE0OTAxfDA&ixlib=rb-4.1.0&q=85',
    color: 'from-cyan-500 to-sky-600',
  },
  {
    id: 'reparaciones',
    icon: Wrench,
    title: 'Reparaciones',
    description: 'Solucionamos fugas, grietas y averías de forma rápida.',
    image: 'https://images.unsplash.com/photo-1638024159638-b2e33e686489?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwxfHxwb29sJTIwdW5kZXJ3YXRlciUyMGJsdWUlMjBsaWdodCUyMGNhdXN0aWNzfGVufDB8fHx8MTc3MzMxODgyOHww&ixlib=rb-4.1.0&q=85',
    color: 'from-blue-500 to-indigo-600',
  },
  {
    id: 'tratamiento',
    icon: Droplets,
    title: 'Tratamiento',
    description: 'Análisis y tratamiento profesional para agua cristalina.',
    image: 'https://images.unsplash.com/photo-1637605938651-24029b90fab9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwyfHxwb29sJTIwdW5kZXJ3YXRlciUyMGJsdWUlMjBsaWdodCUyMGNhdXN0aWNzfGVufDB8fHx8MTc3MzMxODgyOHww&ixlib=rb-4.1.0&q=85',
    color: 'from-teal-500 to-cyan-600',
  },
  {
    id: 'climatizacion',
    icon: ThermometerSun,
    title: 'Climatización',
    description: 'Bombas de calor y paneles solares para todo el año.',
    image: 'https://images.unsplash.com/photo-1770387200416-d499c2af0080?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBzd2ltbWluZyUyMHBvb2wlMjBzdW5zZXQlMjBtZWRpdGVycmFuZWFuJTIwdmlsbGElMjBvY2VhbiUyMHZpZXd8ZW58MHx8fHwxNzczMzE4ODI3fDA&ixlib=rb-4.1.0&q=85',
    color: 'from-orange-500 to-amber-600',
  },
];

// 3D Service Card Component
const ServiceCard3D = ({ service, index, scrollYProgress }) => {
  const cardRef = useRef(null);
  const { scrollYProgress: cardProgress } = useScroll({
    target: cardRef,
    offset: ['start end', 'end start'],
  });

  // 3D transforms based on scroll
  const rotateY = useTransform(cardProgress, [0, 0.5, 1], [index % 2 === 0 ? -15 : 15, 0, index % 2 === 0 ? 5 : -5]);
  const rotateX = useTransform(cardProgress, [0, 0.5, 1], [15, 0, -10]);
  const y = useTransform(cardProgress, [0, 0.3, 0.7, 1], [100, 0, 0, -50]);
  const scale = useTransform(cardProgress, [0, 0.3, 0.7, 1], [0.85, 1, 1, 0.95]);
  const opacity = useTransform(cardProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0.5]);

  const springConfig = { stiffness: 80, damping: 20 };
  const rotateYSpring = useSpring(rotateY, springConfig);
  const ySpring = useSpring(y, springConfig);
  const scaleSpring = useSpring(scale, springConfig);

  const Icon = service.icon;

  return (
    <motion.div
      ref={cardRef}
      style={{
        rotateY: rotateYSpring,
        rotateX,
        y: ySpring,
        scale: scaleSpring,
        opacity,
        transformStyle: 'preserve-3d',
        perspective: 1000,
      }}
      className="group"
      data-testid={`service-card-${service.id}`}
    >
      <div className="relative bg-white rounded-2xl sm:rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-shadow duration-500">
        {/* Image */}
        <div className="relative h-40 sm:h-48 lg:h-56 overflow-hidden">
          <motion.img
            src={service.image}
            alt={service.title}
            className="w-full h-full object-cover"
            whileHover={{ scale: 1.1 }}
            transition={{ duration: 0.6 }}
          />
          <div className={`absolute inset-0 bg-gradient-to-t ${service.color} opacity-40`} />
          
          {/* Icon badge */}
          <motion.div 
            className="absolute top-3 left-3 sm:top-4 sm:left-4"
            whileHover={{ scale: 1.1, rotate: 10 }}
          >
            <div className={`w-10 h-10 sm:w-12 sm:h-12 lg:w-14 lg:h-14 rounded-xl sm:rounded-2xl bg-gradient-to-br ${service.color} flex items-center justify-center shadow-lg`}>
              <Icon className="w-5 h-5 sm:w-6 sm:h-6 lg:w-7 lg:h-7 text-white" />
            </div>
          </motion.div>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-5 lg:p-7">
          <h3 className="font-manrope font-bold text-lg sm:text-xl text-slate-800 mb-2 sm:mb-3 group-hover:text-sky-600 transition-colors">
            {service.title}
          </h3>
          <p className="text-slate-600 text-sm sm:text-base leading-relaxed mb-3 sm:mb-4 line-clamp-2">
            {service.description}
          </p>
          <Link
            to="/servicios"
            className="inline-flex items-center gap-2 text-sky-500 font-semibold text-sm"
          >
            <span>Más info</span>
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>

        {/* 3D Edge highlight */}
        <motion.div
          className="absolute inset-0 rounded-2xl sm:rounded-3xl pointer-events-none"
          style={{
            boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.5)',
          }}
        />
      </div>
    </motion.div>
  );
};

// Floating 3D Pool Tile decoration
const FloatingTile3D = ({ scrollYProgress, position, delay }) => {
  const rotate = useTransform(scrollYProgress, [0, 1], [0, 360]);
  const y = useTransform(scrollYProgress, [0, 1], [0, -200]);
  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [0.8, 1.2, 0.9]);

  return (
    <motion.div
      className="absolute hidden lg:block pointer-events-none"
      style={{
        ...position,
        rotate,
        y,
        scale,
      }}
    >
      <div 
        className="w-16 h-16 rounded-lg bg-gradient-to-br from-sky-200/30 to-cyan-200/30 backdrop-blur-sm border border-sky-200/20"
        style={{
          boxShadow: '0 10px 30px rgba(14,165,233,0.2)',
        }}
      />
    </motion.div>
  );
};

export const ServiceStack = () => {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start end', 'end start'],
  });

  // Header animations
  const headerY = useTransform(scrollYProgress, [0, 0.3], [60, 0]);
  const headerOpacity = useTransform(scrollYProgress, [0, 0.2], [0, 1]);

  const springConfig = { stiffness: 100, damping: 30 };
  const headerYSpring = useSpring(headerY, springConfig);

  return (
    <section 
      ref={containerRef}
      className="py-16 sm:py-24 lg:py-32 bg-gradient-to-b from-white via-sky-50/30 to-white relative overflow-hidden"
      data-testid="services-section"
    >
      {/* Floating decorations */}
      <FloatingTile3D scrollYProgress={scrollYProgress} position={{ top: '10%', left: '5%' }} delay={0} />
      <FloatingTile3D scrollYProgress={scrollYProgress} position={{ top: '30%', right: '8%' }} delay={1} />
      <FloatingTile3D scrollYProgress={scrollYProgress} position={{ bottom: '20%', left: '10%' }} delay={2} />

      {/* Background circles */}
      <motion.div
        style={{
          y: useTransform(scrollYProgress, [0, 1], [0, -150]),
          rotate: useTransform(scrollYProgress, [0, 1], [0, 180]),
        }}
        className="absolute -top-40 -right-40 w-80 h-80 border border-sky-100 rounded-full pointer-events-none hidden lg:block"
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Header */}
        <motion.div
          style={{ y: headerYSpring, opacity: headerOpacity }}
          className="text-center mb-10 sm:mb-16 lg:mb-20"
        >
          <motion.span
            className="inline-flex items-center gap-2 bg-sky-100 text-sky-600 px-4 py-2 rounded-full text-xs sm:text-sm font-semibold mb-4 sm:mb-6"
          >
            <Droplets className="w-3 h-3 sm:w-4 sm:h-4" />
            Nuestros Servicios
          </motion.span>
          
          <h2 className="font-manrope font-extrabold text-3xl sm:text-4xl lg:text-5xl xl:text-6xl text-slate-900 mb-4 sm:mb-6 tracking-tight px-4">
            Todo para tu{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-500 to-cyan-500">
              piscina
            </span>
          </h2>
          
          <p className="text-base sm:text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed px-4">
            Soluciones completas para que disfrutes de tu piscina sin preocupaciones.
          </p>
        </motion.div>

        {/* Services Grid - Responsive */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 sm:gap-6 lg:gap-8">
          {services.map((service, index) => (
            <ServiceCard3D 
              key={service.id} 
              service={service} 
              index={index}
              scrollYProgress={scrollYProgress}
            />
          ))}
        </div>

        {/* CTA */}
        <motion.div
          style={{
            opacity: useTransform(scrollYProgress, [0.5, 0.7], [0, 1]),
            y: useTransform(scrollYProgress, [0.5, 0.7], [30, 0]),
          }}
          className="text-center mt-10 sm:mt-16 lg:mt-20"
        >
          <Link
            to="/servicios"
            data-testid="services-cta"
            className="inline-flex items-center gap-2 sm:gap-3 bg-slate-900 hover:bg-slate-800 text-white px-6 sm:px-8 lg:px-10 py-3 sm:py-4 lg:py-5 rounded-full font-semibold text-sm sm:text-base lg:text-lg transition-all shadow-xl hover:shadow-2xl"
          >
            <span>Ver todos</span>
            <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default ServiceStack;
